import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authAPI } from '../api';
import { getErrorMessage } from '@/api/unwrapQuery';

/**
 * Login hook
 * Handles the complete login flow using TanStack Query mutations
 */
export const useLogin = () => {
    const [step, setStep] = useState('phone'); // 'phone', 'otp', 'mfa', 'profile'
    const [phoneNumber, setPhoneNumber] = useState('');
    const [pendingToken, setPendingToken] = useState(null);

    const sendOTPMutation = useMutation({
        mutationFn: (phone) => authAPI.sendOTP(phone),
    });

    const verifyOTPMutation = useMutation({
        mutationFn: ({ phone, code }) => authAPI.verifyOTP(phone, code),
    });

    const completeProfileMutation = useMutation({
        mutationFn: (profileData) => authAPI.completeProfile(profileData),
    });

    const completeMfaMutation = useMutation({
        mutationFn: ({ factor, payload }) => authAPI.completeMfaLogin(pendingToken, factor, payload),
    });

    /**
     * Send OTP to phone number
     */
    const sendOTP = useCallback(async (phone) => {
        setPhoneNumber(phone);
        try {
            const response = await sendOTPMutation.mutateAsync(phone);

            if (response.success) {
                setStep('otp');
                return { success: true };
            } else {
                throw new Error(response.message || 'خطا در ارسال کد تایید');
            }
        } catch (err) {
            return { success: false, message: getErrorMessage(err, 'خطا در ارسال کد تایید') };
        }
    }, [sendOTPMutation]);

    /**
     * Verify OTP code – cookies set server-side, no token in JSON
     */
    const verifyOTP = useCallback(async (code) => {
        try {
            const response = await verifyOTPMutation.mutateAsync({ phone: phoneNumber, code });

            if (response.success) {
                const { user, isNewUser } = response.data || response;

                if (response.requiresMfa || response.data?.requiresMfa) {
                    const pending = response.pendingToken || response.data?.pendingToken;
                    setPendingToken(pending);
                    setStep('mfa');
                    return { success: true, requiresMfa: true, user: user || response.data?.user };
                }

                if (isNewUser || response.data?.isNewUser) {
                    setStep('profile');
                    return { success: true, needsProfile: true };
                } else {
                    return {
                        success: true,
                        needsProfile: false,
                        user: user || response.data?.user,
                    };
                }
            } else {
                throw new Error(response.message || 'کد تایید نامعتبر است');
            }
        } catch (err) {
            return { success: false, message: getErrorMessage(err, 'کد تایید نامعتبر است') };
        }
    }, [verifyOTPMutation, phoneNumber]);

    /**
     * Get passkey (WebAuthn) options for the login second factor
     */
    const getPasskeyOptions = useCallback(async () => {
        if (!pendingToken) throw new Error('توکن ورود معتبر نیست');
        const response = await authAPI.getLoginMfaPasskeyOptions(pendingToken);
        return response?.options;
    }, [pendingToken]);

    /**
     * Get Dastine options for the login second factor
     * Calls POST /api/auth/mfa/dastine-options then expects {challenge, expiresAt}
     */
    const getDastineOptions = useCallback(async () => {
        if (!pendingToken) throw new Error('توکن ورود معتبر نیست');
        const response = await authAPI.getLoginMfaDastineOptions(pendingToken);
        // Backend returns {success, challenge, expiresAt, mode} or nested data
        return response?.challenge ? response : response?.data;
    }, [pendingToken]);

    /**
     * Complete the login's second factor (TOTP / passkey / recovery)
     */
    const completeMfa = useCallback(async (factor, payload) => {
        try {
            const response = await completeMfaMutation.mutateAsync({ factor, payload });

            if (response.success) {
                const user = response.user || response.data?.user;
                return {
                    success: true,
                    user,
                };
            } else {
                throw new Error(response.message || 'خطا در تأیید هویت');
            }
        } catch (err) {
            return { success: false, message: getErrorMessage(err, 'خطا در تأیید هویت') };
        }
    }, [completeMfaMutation]);

    /**
     * Complete user profile – cookies set server-side
     */
    const completeProfile = useCallback(async (profileData) => {
        try {
            const response = await completeProfileMutation.mutateAsync(profileData);

            if (response.success) {
                const user = response.user || response.data?.user;
                return {
                    success: true,
                    user,
                };
            } else {
                throw new Error(response.message || 'خطا در ثبت اطلاعات');
            }
        } catch (err) {
            return { success: false, message: getErrorMessage(err, 'خطا در ثبت اطلاعات') };
        }
    }, [completeProfileMutation]);

    /**
     * Reset login flow
     */
    const reset = useCallback(() => {
        setStep('phone');
        setPhoneNumber('');
        setPendingToken(null);
        sendOTPMutation.reset();
        verifyOTPMutation.reset();
        completeProfileMutation.reset();
        completeMfaMutation.reset();
    }, [sendOTPMutation, verifyOTPMutation, completeProfileMutation, completeMfaMutation]);

    /**
     * Go back to previous step
     */
    const goBack = useCallback(() => {
        if (step === 'otp') {
            setStep('phone');
        } else if (step === 'mfa') {
            setStep('otp');
        } else if (step === 'profile') {
            setStep('otp');
        }
    }, [step]);

    const error =
        getErrorMessage(sendOTPMutation.error, 'خطا در ارسال کد تایید') ||
        getErrorMessage(verifyOTPMutation.error, 'کد تایید نامعتبر است') ||
        getErrorMessage(completeMfaMutation.error, 'خطا در تأیید هویت') ||
        getErrorMessage(completeProfileMutation.error, 'خطا در ثبت اطلاعات') ||
        null;

    return {
        loading:
            sendOTPMutation.isPending ||
            verifyOTPMutation.isPending ||
            completeMfaMutation.isPending ||
            completeProfileMutation.isPending,
        error,
        step,
        phoneNumber,
        pendingToken,
        sendOTP,
        verifyOTP,
        getPasskeyOptions,
        getDastineOptions,
        completeMfa,
        completeProfile,
        reset,
        goBack,
    };
};

export default useLogin;